import { WatchQueryOptions } from '@apollo/client';
import { useRouter } from 'next/router';
// Types
import { StreamStatus } from 'api/graphql-global-types';
import {
  GetStream,
  GetStreamVariables,
  GetStream_stream,
} from 'api/streams/types/GetStream';
// Hooks
import { useBookFreeStream, useGetCurrUser, useGuestPpvBuyFlow } from 'hooks';
// Helpers
import {
  computeOrderStreamPath,
  computeVideoWatchStreamPath,
} from 'helpers/routes';
import { formatPrice } from 'helpers/formatPrice';
// UI3
import Button from 'ui3/Button/Button';
import LinkButton from 'ui3/LinkButton/LinkButton';
// Components
import SocialShareQRButton from 'components/common3/SocialShareQRButton/SocialShareQRButton';
import UpcomingWatchActions from './components/UpcomingWatchActions/UpcomingWatchActions';
// Constants
import { STREAM_CHECKOUT } from 'constants/routes';
// Styles
import styles from './OrderStreamPayments.module.scss';

/** An ended stream is a recording — the action on it is playback, not "watch a stream". */
const PLAY_RECORDING_LABEL = 'Play';

type OrderStreamPaymentsProps = {
  stream: GetStream_stream;
  updateStreamQuery: <TVars = GetStreamVariables>(
    mapFn: (
      previousQueryResult: GetStream,
      options: Pick<WatchQueryOptions<TVars, GetStream>, 'variables'>
    ) => GetStream
  ) => void;
  socialShareButtonText: string;
  isInterview?: boolean;
};

const OrderStreamPayments: React.FC<OrderStreamPaymentsProps> = ({
  stream,
  socialShareButtonText,
  isInterview = false,
}) => {
  const bookFreeStream = useBookFreeStream();
  const { startGuestPpvBuy } = useGuestPpvBuyFlow();
  const { data: userData } = useGetCurrUser();
  const { push } = useRouter();
  const {
    id: streamId,
    isFree,
    price,
    isPurchased,
    streamStatus,
    store,
  } = stream;

  const isStreamActive = streamStatus === StreamStatus.Active;
  const isStreamUpcoming = streamStatus === StreamStatus.Scheduled;
  const isStreamEnded = streamStatus === StreamStatus.Ended;

  const streamTypeText = isInterview ? 'Interview' : 'Stream';
  const isUserStreamCreator = userData?.me?.id === store?.id;
  const hasAccessToWatch = isPurchased || isUserStreamCreator;

  const watchStreamPath = computeVideoWatchStreamPath(stream);

  const handleCheckout = () => {
    if (!userData) {
      startGuestPpvBuy(streamId);
    } else {
      push(`${STREAM_CHECKOUT}/${streamId}/information`);
    }
  };

  const handleRegisterStreamClick = () => {
    if (!userData && isStreamUpcoming) {
      startGuestPpvBuy(streamId);
    } else {
      bookFreeStream(stream);
    }
  };

  const buyButtonClick = () => {
    if (isFree) {
      handleRegisterStreamClick();
    } else {
      handleCheckout();
    }
  };

  // Upcoming streams the viewer can watch (creator, purchased, or free) show a
  // "Watch Stream" button that stays locked until 1 hour before start, with a
  // tooltip and a live countdown to the scheduled start time.
  const isUpcomingWatchable = isStreamUpcoming && (hasAccessToWatch || isFree);

  if (isUpcomingWatchable) {
    return (
      <div className={styles.actionFooter}>
        <UpcomingWatchActions
          stream={stream}
          watchStreamPath={watchStreamPath}
          socialShareButtonText={socialShareButtonText}
          hasDirectAccess={hasAccessToWatch}
          onWatch={buyButtonClick}
        />
      </div>
    );
  }

  if (hasAccessToWatch) {
    // An ended stream is a recording, so the action is playback, not "watch a
    // stream" — the same wording the card overlay uses.
    const watchButtonLabel = isStreamActive
      ? `Join the ${streamTypeText}`
      : isStreamEnded
      ? PLAY_RECORDING_LABEL
      : `Watch the ${streamTypeText}`;

    return (
      <div className={styles.actionFooter}>
        <div className={styles.actionFooterButtons}>
          <LinkButton link={watchStreamPath} variant="primary">
            {watchButtonLabel}
          </LinkButton>
          {stream && (
            <SocialShareQRButton
              onlyIcon
              itemTitle={stream?.name || ''}
              modalTitle="Share Stream"
              modalText="Share this stream in any way convenient for you"
              path={computeOrderStreamPath(stream)}
              twitterText={socialShareButtonText || ''}
            />
          )}
        </div>
      </div>
    );
  }

  const streamPrice = formatPrice(price);

  const getBuyButtonText = () => {
    // `isFree` rather than a zero price: an auto-freed PPV recording keeps its
    // original price, so a price check here would still read as "Buy".
    if (isFree) {
      return isStreamEnded ? PLAY_RECORDING_LABEL : `Watch ${streamTypeText}`;
    }

    return isStreamEnded
      ? `Buy Past ${streamTypeText} - ${streamPrice}`
      : `Buy ${streamTypeText} - ${streamPrice}`;
  };

  const buyButtonText = getBuyButtonText();

  return (
    <div className={styles.actionFooter}>
      <div className={styles.actionFooterButtons}>
        <Button onClick={buyButtonClick} className={styles.buyButton}>
          {buyButtonText}
        </Button>
        {stream && (
          <SocialShareQRButton
            onlyIcon
            itemTitle={stream?.name || ''}
            modalTitle="Share Stream"
            modalText="Share this stream in any way convenient for you"
            path={computeOrderStreamPath(stream)}
            twitterText={socialShareButtonText || ''}
          />
        )}
      </div>
    </div>
  );
};

export default OrderStreamPayments;
